import { ChangeEventHandler, FC, useCallback } from 'react';

import { FiBriefcase, FiLock, FiMail, FiPhone, FiUser } from 'react-icons/fi';
import { useForm } from 'react-hook-form';

import Link from 'next/link';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { routes } from '~/shared/constants/routes';

import { useAuth } from '~/modules/auth/hooks/useAuth';

import { FormGroup } from '~/shared/components/Form/FormGroup';
import { ControlledInput } from '~/shared/components/Form/ControlledInput';
import { Button } from '~/shared/components/Button';
import { AuthTitle } from '~/shared/components/AuthLayout/AuthTitle';
import { AuthForm } from '~/shared/components/AuthLayout/AuthForm';

import {
  maskCellPhone,
  maskNaturalDocument,
  unmask,
} from '~/shared/utils/masks';

import { ISignUpCredentials } from '../../interfaces/ISignUpCredentials';

import { FormFooter } from './styles';

const schemaValidation = yup.object().shape({
  name: yup.string().required('Nome completo é obrigatório'),
  phoneNumber: yup
    .string()
    .phoneNumber('Número de telefone inválido')
    .required('Número de telefone é obrigatório'),
  document: yup
    .string()
    .naturalDocument('CPF inválido')
    .required('CPF é obrigatório'),
  email: yup
    .string()
    .email('O formato do e-mail está incorreto')
    .required('E-mail é obrigatório'),
  password: yup.string().required('Senha é obrigatório'),
  passwordConfirmation: yup
    .string()
    .required('Confirmação da senha é obrigatório')
    .oneOf([yup.ref('password'), null], 'As senha não conferem'),
});

export const SignUp: FC = () => {
  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    setValue,
  } = useForm<ISignUpCredentials>({
    resolver: yupResolver(schemaValidation),
  });

  const { signUp } = useAuth();

  const handleChangeDocument: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        const unmaskedValue = unmask(event.target.value);

        const formattedDocument = maskNaturalDocument(unmaskedValue);

        setValue('document', formattedDocument, { shouldValidate: true });
      },
      [setValue]
    );

  const handleChangePhoneNumber: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        const unmaskedValue = unmask(event.target.value);

        const formattedPhoneNumber = maskCellPhone(unmaskedValue);

        setValue('phoneNumber', formattedPhoneNumber, { shouldValidate: true });
      },
      [setValue]
    );

  const handleSignUp = handleSubmit(signUp);

  return (
    <>
      <AuthTitle title="Cria sua conta na plataforma" />

      <AuthForm onSubmit={handleSignUp}>
        <FormGroup>
          <ControlledInput
            control={control}
            name="name"
            icon={FiUser}
            placeholder="Digite seu nome completo"
            size="lg"
            error={errors.name}
          />
        </FormGroup>

        <FormGroup>
          <ControlledInput
            control={control}
            name="document"
            icon={FiBriefcase}
            placeholder="Digite seu CPF"
            size="lg"
            error={errors.document}
            onChange={handleChangeDocument}
            disableHookFormOnChange
          />
        </FormGroup>

        <FormGroup>
          <ControlledInput
            control={control}
            name="phoneNumber"
            icon={FiPhone}
            placeholder="Digite seu telefone"
            size="lg"
            error={errors.phoneNumber}
            disableHookFormOnChange
            onChange={handleChangePhoneNumber}
          />
        </FormGroup>

        <FormGroup>
          <ControlledInput
            control={control}
            name="email"
            icon={FiMail}
            placeholder="Digite seu e-mail"
            inputMode="email"
            autoComplete="email"
            size="lg"
            error={errors.email}
          />
        </FormGroup>

        <FormGroup>
          <ControlledInput
            control={control}
            name="password"
            icon={FiLock}
            placeholder="Digite sua senha"
            autoComplete="new-password"
            size="lg"
            error={errors.password}
            type="password"
          />
        </FormGroup>

        <FormGroup>
          <ControlledInput
            control={control}
            name="passwordConfirmation"
            icon={FiLock}
            placeholder="Digite sua senha novamente"
            autoComplete="new-password"
            size="lg"
            error={errors.passwordConfirmation}
            type="password"
          />
        </FormGroup>

        <Button type="submit" isLoading={isSubmitting} size="lg" mt="md">
          Cadastrar
        </Button>

        <FormFooter>
          <span>
            Já tem uma conta? <Link href={routes.AUTH.SIGN_IN}>Entrar</Link>
          </span>
        </FormFooter>
      </AuthForm>
    </>
  );
};
